import type { ModelAction } from '../../../types/responses/ModelAction';
import type { PDFNote } from '../models/PDFNote';
import type { PDFNoteQuery } from '../models/PDFNoteQuery';
import type { PDFNoteRefreshPageRequest, PDFNoteRefreshPageResponse } from '../models/PDFNoteRefreshPage';
import { normalizeNoteIds } from './noteUpdates';

export type NotePageSnapshot = PDFNoteRefreshPageResponse;
export type NotePageLoadedData = Pick<NotePageSnapshot, 'Items' | 'TotalCount' | 'Skipped'> & {
  Actions?: ModelAction[];
  Take?: number;
  PageIds?: number[];
};

export class NotePageConsistencyError extends Error {}

const MAX_CHANGED_IDS = 1000;

export interface NotePageLoadToken {
  readonly queryKey: string;
  readonly generation: number;
  readonly load: number;
}

interface NotePageUpdatesOptions {
  request(request: PDFNoteRefreshPageRequest): Promise<PDFNoteRefreshPageResponse>;
  onPage(page: NotePageSnapshot, query: PDFNoteQuery): void;
  onError(error: unknown): void;
}

export interface NotePageUpdates {
  setQuery(query: PDFNoteQuery): void;
  setEnabled(enabled: boolean): void;
  beginLoad(query: PDFNoteQuery): NotePageLoadToken;
  acceptPage(token: NotePageLoadToken, page: NotePageLoadedData): boolean;
  refresh(ids: readonly number[]): void;
  retry(): void;
  consumeQueryEcho(query: PDFNoteQuery): boolean;
  suspend(): void;
  dispose(): void;
}

export function noteQueryKey(query: PDFNoteQuery): string {
  return JSON.stringify(query);
}

export function mergeNotePage(previous: NotePageSnapshot, response: PDFNoteRefreshPageResponse): NotePageSnapshot {
  if (!Number.isSafeInteger(response.TotalCount) || response.TotalCount < 0
    || !Number.isSafeInteger(response.Skipped) || response.Skipped < 0
    || !Number.isSafeInteger(response.Take) || response.Take <= 0
    || response.PageIds.length > response.Take) {
    throw new NotePageConsistencyError('Сервер вернул некорректные параметры страницы замечаний');
  }
  if (response.PageIds.length !== Math.min(response.Take, Math.max(0, response.TotalCount - response.Skipped))) {
    throw new NotePageConsistencyError('Счётчик замечаний не соответствует странице');
  }
  const pageIds = normalizeNoteIds(response.PageIds);
  if (pageIds.length !== response.PageIds.length) throw new NotePageConsistencyError('Сервер вернул некорректные идентификаторы страницы замечаний');
  const pageIdSet = new Set(pageIds);
  const replacements = new Map<number, PDFNote>();
  for (const note of response.Items) {
    if (note.Id === undefined || !pageIdSet.has(note.Id) || replacements.has(note.Id)) {
      throw new NotePageConsistencyError('Сервер вернул некорректные строки страницы замечаний');
    }
    replacements.set(note.Id, note);
  }
  const existing = new Map(previous.Items.map(note => [note.Id, note]));
  const items = pageIds.map(id => {
    const note = replacements.get(id) ?? existing.get(id);
    if (!note) throw new NotePageConsistencyError('В ответе сервера отсутствует строка страницы замечаний');
    return note;
  });
  const actions = [
    ...previous.Actions.filter(action => pageIdSet.has(action.ObjectId) && !replacements.has(action.ObjectId)),
    ...response.Actions.filter(action => replacements.has(action.ObjectId)),
  ];
  return { ...response, Items: items, Actions: actions };
}

export async function readNotePage(
  request: PDFNoteRefreshPageRequest,
  fetchPage: (request: PDFNoteRefreshPageRequest) => Promise<PDFNoteRefreshPageResponse>,
  previous: NotePageSnapshot = { PageIds: [], Items: [], Actions: [], TotalCount: 0, Skipped: 0, Take: request.Query.Take ?? 20 },
  isCurrent: () => boolean = () => true,
): Promise<NotePageSnapshot | undefined> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const response = await fetchPage(request);
    if (!isCurrent()) return undefined;
    try {
      const returnedIds = new Set(response.Items.map(note => note.Id));
      const pageIds = new Set(response.PageIds);
      if (request.ChangedIds.some(id => pageIds.has(id) && !returnedIds.has(id))) {
        throw new NotePageConsistencyError('В ответе сервера отсутствует изменённое замечание');
      }
      return mergeNotePage(previous, response);
    } catch (error) {
      if (!(error instanceof NotePageConsistencyError) || attempt > 0) throw error;
    }
  }
  return undefined;
}

export function createNotePageUpdates(options: NotePageUpdatesOptions): NotePageUpdates {
  let query: PDFNoteQuery | undefined;
  let queryKey = '';
  let generation = 0;
  let load = 0;
  let enabled = true;
  let disposed = false;
  let scheduled = false;
  let running = false;
  let blocked = false;
  let snapshot: NotePageSnapshot | undefined;
  let queryEcho: string | undefined;
  const pending = new Set<number>();

  const schedule = () => {
    if (scheduled || running || disposed || !enabled || blocked || !snapshot || pending.size === 0) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      void run();
    });
  };

  const run = async () => {
    if (running || disposed || !enabled || blocked || !query || !snapshot || pending.size === 0) return;
    const currentGeneration = generation;
    const currentLoad = load;
    const currentQuery = query;
    const currentSnapshot = snapshot;
    const ids = [...pending].slice(0, MAX_CHANGED_IDS);
    ids.forEach(id => pending.delete(id));
    running = true;
    try {
      const next = await readNotePage({
        Query: currentQuery,
        KnownIds: currentSnapshot.PageIds,
        ChangedIds: ids,
      }, options.request, currentSnapshot, () => !disposed && currentGeneration === generation && currentLoad === load);
      if (!next) return;
      const nextQuery = { ...currentQuery, Skip: next.Skipped, Take: next.Take };
      const nextKey = noteQueryKey(nextQuery);
      if (nextKey !== queryKey) queryEcho = nextKey;
      query = nextQuery;
      queryKey = nextKey;
      snapshot = next;
      options.onPage(next, nextQuery);
    } catch (error) {
      if (disposed || currentGeneration !== generation || currentLoad !== load) return;
      ids.forEach(id => pending.add(id));
      blocked = true;
      options.onError(error);
    } finally {
      running = false;
      schedule();
    }
  };

  const setQuery = (next: PDFNoteQuery) => {
    const nextKey = noteQueryKey(next);
    if (nextKey === queryKey) return;
    generation += 1;
    query = { ...next };
    queryKey = nextKey;
    snapshot = undefined;
    queryEcho = undefined;
    blocked = false;
    pending.clear();
  };

  const suspend = () => {
    enabled = false;
    generation += 1;
    pending.clear();
    snapshot = undefined;
    queryEcho = undefined;
  };

  return {
    setQuery,
    setEnabled(value) {
      if (!value && enabled) suspend();
      enabled = value;
      schedule();
    },
    beginLoad(nextQuery) {
      setQuery(nextQuery);
      load += 1;
      snapshot = undefined;
      return { queryKey, generation, load };
    },
    acceptPage(token, page) {
      if (disposed || token.queryKey !== queryKey || token.generation !== generation || token.load !== load || !query) return false;
      const response: NotePageSnapshot = {
        ...page,
        PageIds: page.PageIds ?? normalizeNoteIds(page.Items.flatMap(note => note.Id === undefined ? [] : [note.Id])),
        Actions: page.Actions ?? [],
        Take: page.Take ?? query.Take ?? 20,
      };
      const next = mergeNotePage({ ...response, Items: [], Actions: [], PageIds: [] }, response);
      const nextQuery = { ...query, Skip: next.Skipped, Take: next.Take };
      const nextKey = noteQueryKey(nextQuery);
      if (nextKey !== queryKey) queryEcho = nextKey;
      query = nextQuery;
      queryKey = nextKey;
      snapshot = next;
      load += 1;
      blocked = false;
      schedule();
      return true;
    },
    refresh(ids) {
      normalizeNoteIds(ids).forEach(id => pending.add(id));
      blocked = false;
      schedule();
    },
    retry() {
      blocked = false;
      schedule();
    },
    consumeQueryEcho(nextQuery) {
      const matches = queryEcho !== undefined && noteQueryKey(nextQuery) === queryEcho;
      queryEcho = undefined;
      return matches;
    },
    suspend,
    dispose() {
      disposed = true;
      suspend();
    },
  };
}
